import { useTranslation } from 'react-i18next';

import { hasBlockOptions, isCompletion, TYPE_LABEL_KEY, type StudentTask } from '@/api/ieltsMock';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { CLOSED_CHOICES } from '@/lib/answerWidget';
import { questionNumber } from '@/lib/mockReview';
import { cn } from '@/lib/utils';

/**
 * Прежняя карточка задания — дословно та, что была в StudentIELTSMockPage до 21.10.2026.
 * С 27.10.2026 (iOS 1.1.2) только для видов, которых нет в `wideKind`: сопоставление,
 * True/False/Not Given — на любой ширине, как на сайте. Виды с полем, выбор A/B/C и (с 30.10.2026)
 * «выберите ДВА» и на узком экране идут в новую карточку, поэтому ветки `isCompletion`, `mc_single` и
 * `mc_multi` ниже сейчас не срабатывают — оставлены страховкой на случай, если вид уберут из `wideKind`.
 * Выбор между карточками — `TaskCard` в `MockTaskCard.tsx`.
 */

const LETTERS = 'ABCDEFGHIJKLMNOP'.split('');

// Подпись варианта в списке. У сопоставления с абзацами сами варианты И ЕСТЬ
// буквы («A», «B», «C»), и приписывание буквы-маркера давало «A A» и «C. C».
// Если текст варианта совпадает с его буквой, показываем только букву.
function optionLabel(letter: string, text: string, withDot: boolean): string {
  if (text.trim().toUpperCase() === letter) return letter;
  return withDot ? `${letter}. ${text}` : `${letter} ${text}`;
}

export function LegacyTaskCard({
  task,
  answers,
  onAnswer,
}: {
  task: StudentTask;
  answers: Record<string, string>;
  onAnswer: (qid: string, val: string) => void;
}) {
  const { t } = useTranslation();
  const block = task.options ?? [];
  // Картинка лежит у вопросов (место под неё в схеме именно там), но относится
  // ко всему заданию — берём первую попавшуюся.
  const planImage = task.questions.find((q) => q.image_url)?.image_url;
  return (
    <Card variant="subtle" className="space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Pill variant="primary">{t(TYPE_LABEL_KEY[task.type] ?? task.type)}</Pill>
          {task.group_label && <span className="text-xs text-on-surface-variant">{task.group_label}</span>}
        </div>
        {task.instruction && <p className="text-sm text-on-surface-variant">{task.instruction}</p>}
      </div>

      {/* План или схема для заданий с картой. Картинка одна на задание, хотя
          хранится у каждого вопроса — показываем её единожды, сверху, чтобы
          ученик видел план целиком, а не по кусочку над каждым вопросом. */}
      {planImage && (
        <figure className="overflow-x-auto rounded-lg border border-outline bg-surface/60 p-2">
          <img
            src={planImage}
            alt={task.instruction || t('ieltsMock.planAlt')}
            className="mx-auto h-auto w-full max-w-[720px]"
            loading="lazy"
          />
        </figure>
      )}

      {hasBlockOptions(task.type) && block.length > 0 && (
        <ul className="rounded-lg border border-outline bg-surface/60 p-3 text-sm space-y-1">
          {block.map((o, i) => (
            <li key={i}>
              <span className="font-semibold text-primary mr-1.5">{LETTERS[i]}</span>
              {o.trim().toUpperCase() !== LETTERS[i] && o}
            </li>
          ))}
        </ul>
      )}

      <ol className="space-y-4">
        {task.questions.map((q) => {
          const val = answers[q.id] ?? '';
          return (
            <li key={q.id} className="space-y-2">
              <div className="flex gap-2 text-sm font-medium">
                <span className="text-primary">{questionNumber(q.ord, q.ord_to)}.</span>
                <span>{q.prompt}</span>
              </div>

              {/* mc_single → radios over per-question options (answer = option text) */}
              {task.type === 'mc_single' &&
                (q.options ?? []).map((opt, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm cursor-pointer pl-5">
                    <input
                      type="radio"
                      name={q.id}
                      checked={val === opt}
                      onChange={() => onAnswer(q.id, opt)}
                      className="mt-0.5 accent-primary"
                    />
                    <span>{opt}</span>
                  </label>
                ))}

              {/* mc_multi → checkboxes; value = sorted " || "-joined option texts.
                  «Выберите ДВА» — не больше двух, как на бланке (20.10.2026):
                  лишний вариант сервер снимает баллом, третью галочку не даём. */}
              {task.type === 'mc_multi' &&
                (q.options ?? []).map((opt, i) => {
                  const chosen = val ? val.split('||').map((s) => s.trim()) : [];
                  const on = chosen.includes(opt);
                  const full = !on && chosen.length >= 2;
                  return (
                    <label
                      key={i}
                      className={cn('flex items-start gap-2 text-sm pl-5', full ? 'opacity-50' : 'cursor-pointer')}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={full}
                        onChange={() => {
                          const next = on ? chosen.filter((c) => c !== opt) : [...chosen, opt];
                          onAnswer(q.id, next.sort().join(' || '));
                        }}
                        className="mt-0.5 accent-primary"
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}

              {/* true_false_ng / yes_no_ng → radios over the fixed enum (общий с тренажёром, lib/answerWidget.ts) */}
              {(task.type === 'true_false_ng' || task.type === 'yes_no_ng') &&
                (CLOSED_CHOICES[task.type] ?? []).map(([v, label]) => (
                  <label key={v} className="inline-flex items-center gap-1.5 text-sm cursor-pointer mr-4 pl-5">
                    <input
                      type="radio"
                      name={q.id}
                      checked={val === v}
                      onChange={() => onAnswer(q.id, v)}
                      className="accent-primary"
                    />
                    <span>{label}</span>
                  </label>
                ))}

              {/* block-option types → <select> over the shared options (answer = text) */}
              {hasBlockOptions(task.type) && (
                <select
                  value={val}
                  onChange={(e) => onAnswer(q.id, e.target.value)}
                  className="ml-5 rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
                >
                  <option value="">{t('mock.choose')}</option>
                  {block.map((o, i) => (
                    <option key={i} value={o}>
                      {optionLabel(LETTERS[i], o, true)}
                    </option>
                  ))}
                </select>
              )}

              {/* completion / short-answer → free text input */}
              {isCompletion(task.type) && (
                <input
                  type="text"
                  value={val}
                  onChange={(e) => onAnswer(q.id, e.target.value)}
                  placeholder={t('ieltsMock.yourAnswer')}
                  className="ml-5 w-full max-w-sm rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
                />
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
